import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  fetchInboxNotifications,
  fetchInboxUnreadCount,
  markInboxNotificationRead,
  markInboxReadAll,
} from '../services/notifications';
import {
  loadStoredNotifications,
  mergeNotifications,
  normalizeFcmPayload,
  onInboxNotification,
  parseNotificationsList,
  parseUnreadCount,
  saveStoredNotifications,
} from '../utils/notificationInbox';

const AdminInboxContext = createContext(null);

export const useAdminInbox = () => {
  const ctx = useContext(AdminInboxContext);
  if (!ctx) {
    throw new Error('useAdminInbox must be used within AdminInboxProvider');
  }
  return ctx;
};

export const AdminInboxProvider = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [notifications, setNotifications] = useState(() => loadStoredNotifications());
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxLoading, setInboxLoading] = useState(false);

  const persist = useCallback((list) => {
    setNotifications(list);
    saveStoredNotifications(list);
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await fetchInboxUnreadCount();
      setUnreadCount(parseUnreadCount(res));
    } catch (err) {
      console.warn('Unread count fetch failed:', err);
      setUnreadCount((prev) => prev);
    }
  }, []);

  const addNotification = useCallback(
    (item) => {
      if (!item?.id) return;
      persist(mergeNotifications(loadStoredNotifications(), [item]));
      if (!item.read) {
        setUnreadCount((c) => c + 1);
      }
    },
    [persist],
  );

  const addFromFcmPayload = useCallback(
    (payload) => {
      addNotification(normalizeFcmPayload(payload));
    },
    [addNotification],
  );

  const refreshFromApi = useCallback(async () => {
    setInboxLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        fetchInboxNotifications({ limit: 50, offset: 0 }),
        fetchInboxUnreadCount(),
      ]);
      const fromApi = parseNotificationsList(listRes);
      if (fromApi.length) {
        persist(mergeNotifications(loadStoredNotifications(), fromApi));
      }
      setUnreadCount(parseUnreadCount(countRes));
    } catch (err) {
      console.warn('Inbox fetch failed:', err);
    } finally {
      setInboxLoading(false);
    }
  }, [persist]);

  useEffect(() => {
    if (loading || !isAuthenticated) return undefined;
    refreshFromApi();
    const unsubBus = onInboxNotification((item) => addNotification(item));
    return () => unsubBus();
  }, [loading, isAuthenticated, refreshFromApi, addNotification]);

  const markAllRead = useCallback(async () => {
    try {
      await markInboxReadAll();
    } catch (err) {
      console.warn('Mark all read failed:', err);
    }
    persist(notifications.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [notifications, persist]);

  const clearAll = useCallback(() => {
    persist([]);
  }, [persist]);

  const markRead = useCallback(
    async (id) => {
      const target = notifications.find((n) => n.id === id);
      try {
        await markInboxNotificationRead(id);
      } catch (err) {
        console.warn('Mark read failed:', err);
      }
      persist(notifications.map((n) => (n.id === id ? { ...n, read: true } : n)));
      if (target && !target.read) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    },
    [notifications, persist],
  );

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      inboxLoading,
      refreshFromApi,
      refreshUnreadCount,
      addFromFcmPayload,
      markAllRead,
      clearAll,
      markRead,
    }),
    [
      notifications,
      unreadCount,
      inboxLoading,
      refreshFromApi,
      refreshUnreadCount,
      addFromFcmPayload,
      markAllRead,
      clearAll,
      markRead,
    ],
  );

  return <AdminInboxContext.Provider value={value}>{children}</AdminInboxContext.Provider>;
};
